import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { User, Lock, Shield, Users, Plus, Trash2, Edit2, UserPlus } from "lucide-react";

const SettingsPage = () => {
  const { user } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: "",
    email: "",
    role: "staff"
  });

  const isAdmin = user?.role === 'admin';

  const fetchUsers = async () => {
    if (!isAdmin) return;
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { withCredentials: true });
      
      toast.success("Password changed successfully");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleInviteStaff = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/users/invite`, inviteForm, { withCredentials: true });
      toast.success(`Staff invited! Temporary password: ${response.data.temp_password}`);
      setInviteDialogOpen(false);
      setInviteForm({ name: "", email: "", role: "staff" });
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite staff");
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}`, { role: newRole }, { withCredentials: true });
      toast.success("User role updated");
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success("User deleted");
      await fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="font-outfit text-2xl font-medium text-zinc-900">Settings</h1>
        <p className="text-muted-foreground text-sm">Manage your account and team</p>
      </div>

      {/* Profile Info */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-primary" />
          <h2 className="font-outfit font-medium">Profile Information</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input value={user?.name || ""} disabled className="bg-zinc-50" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ""} disabled className="bg-zinc-50" />
          </div>
          <div>
            <Label>Role</Label>
            <Input value={user?.role || "admin"} disabled className="bg-zinc-50 capitalize" />
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-5 h-5 text-primary" />
          <h2 className="font-outfit font-medium">Change Password</h2>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="current_password">Current Password</Label>
            <Input
              id="current_password"
              type="password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
              required
              data-testid="current-password-input"
            />
          </div>
          <div>
            <Label htmlFor="new_password">New Password</Label>
            <Input
              id="new_password"
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
              required
              data-testid="new-password-input"
            />
          </div>
          <div>
            <Label htmlFor="confirm_password">Confirm New Password</Label>
            <Input
              id="confirm_password"
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
              required
              data-testid="confirm-password-input"
            />
          </div>
          <Button type="submit" disabled={loading} data-testid="change-password-btn">
            {loading ? "Changing..." : "Change Password"}
          </Button>
        </form>
      </div>

      {/* Team Management (Admin Only) */}
      {isAdmin && (
        <div className="bg-white border border-zinc-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <h2 className="font-outfit font-medium">Team Management</h2>
            </div>
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2" data-testid="invite-staff-btn">
                  <UserPlus className="w-4 h-4" />
                  Invite Staff
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Staff Member</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInviteStaff} className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                      required
                      data-testid="invite-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                      required
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({...inviteForm, role: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="staff">Staff</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Staff can manage invoices, customers, and items. Admins have full access.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="send-invite-btn">Send Invite</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {usersLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : users.length === 0 ? (
            <p className="text-muted-foreground">No team members yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Role</th>
                    <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-zinc-100">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{u.name}</span>
                          {u.user_id === user?.user_id && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5">You</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">{u.email}</td>
                      <td className="py-3 px-4">
                        {u.user_id !== user?.user_id ? (
                          <Select value={u.role} onValueChange={(v) => handleUpdateRole(u.user_id, v)}>
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="staff">Staff</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="capitalize text-muted-foreground">{u.role}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {u.user_id !== user?.user_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(u.user_id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Security Info */}
      <div className="bg-white border border-zinc-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-primary" />
          <h2 className="font-outfit font-medium">Security</h2>
        </div>
        <div className="text-sm text-muted-foreground space-y-2">
          <p>• Your password is securely hashed using bcrypt</p>
          <p>• Sessions expire after 7 days of inactivity</p>
          <p>• All data is encrypted in transit and at rest</p>
          <p>• Payments are processed securely via Stripe</p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
